import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCards,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DECK_STATUS_LABELS, UPLOAD_IMAGE_MIME_TYPES, type AccessRule } from '@creativitas/shared';
import { PageHeader } from '@/components/ui/PageHeader';
import { AssetImage } from '@/components/ui/AssetImage';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/StateViews';
import { toUserMessage } from '@/lib/api';
import { ADMIN_CONTENT_KEY, fetchAdminContent } from './api';
import { AccessRulesEditor } from './AccessRulesEditor';
import { CardSorter } from './CardSorter';
import {
  ADMIN_DECKS_KEY,
  deleteCard,
  deleteDeck,
  fetchAdminDeck,
  importCards,
  reorderCards,
  setAccessRules,
  updateDeck,
  uploadImage,
} from './content-api';

/** Deck editor: cards, order, images, access and publication (§18). */
export function AdminDeckEditorPage() {
  const { deckId = '' } = useParams();
  /* «?tab=access» — прямой переход к доступу из списка. */
  const [searchParams] = useSearchParams();
  const initialTab = ['cards', 'access', 'settings'].includes(searchParams.get('tab') ?? '')
    ? searchParams.get('tab')!
    : 'cards';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const deckKey = [...ADMIN_DECKS_KEY, deckId];
  const query = useQuery({ queryKey: deckKey, queryFn: () => fetchAdminDeck(deckId) });
  const contentQuery = useQuery({ queryKey: ADMIN_CONTENT_KEY, queryFn: fetchAdminContent });

  const [title, setTitle] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);

  const deck = query.data;
  const games = (contentQuery.data?.items ?? []).filter((item) => item.kind === 'game' && item.variant !== 'demo');

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: deckKey });
    void queryClient.invalidateQueries({ queryKey: ADMIN_DECKS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['decks'] });
  };

  const fail = (title: string) => (error: unknown) =>
    notifications.show({ title, message: toUserMessage(error), color: 'red' });

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateDeck>[1]) => updateDeck(deckId, input),
    onSuccess: (updated) => {
      queryClient.setQueryData(deckKey, updated);
      invalidate();
      notifications.show({ message: 'Колода обновлена', color: 'teal' });
    },
    onError: fail('Не удалось сохранить колоду'),
  });

  const importMutation = useMutation({
    mutationFn: (files: File[]) => importCards(deckId, files),
    onSuccess: (result) => {
      queryClient.setQueryData(deckKey, result.deck);
      invalidate();
      notifications.show({
        message:
          result.skipped.length === 0
            ? `Добавлено карт: ${result.added}`
            : `Добавлено: ${result.added}. Пропущено: ${result.skipped.length}`,
        color: result.skipped.length === 0 ? 'teal' : 'gold',
      });
    },
    onError: fail('Не удалось загрузить карты'),
  });

  const reorderMutation = useMutation({
    mutationFn: (cardIds: string[]) => reorderCards(deckId, cardIds),
    onSuccess: (updated) => {
      queryClient.setQueryData(deckKey, updated);
      invalidate();
    },
    onError: fail('Не удалось изменить порядок'),
  });

  const deleteCardMutation = useMutation({
    mutationFn: deleteCard,
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Карта удалена', color: 'teal' });
    },
    onError: fail('Не удалось удалить карту'),
  });

  const coverMutation = useMutation({
    mutationFn: async ({ file, field }: { file: File; field: 'coverImage' | 'cardBackImage' }) => {
      const stored = await uploadImage(file);
      return updateDeck(deckId, { [field]: stored.url });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(deckKey, updated);
      invalidate();
      notifications.show({ message: 'Изображение обновлено', color: 'teal' });
    },
    onError: fail('Не удалось загрузить изображение'),
  });

  const accessMutation = useMutation({
    mutationFn: ({ rules, gameId }: { rules: AccessRule[]; gameId: string | null }) =>
      setAccessRules('deck', deckId, rules, gameId),
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Правила доступа сохранены', color: 'teal' });
    },
    onError: fail('Не удалось сохранить доступ'),
  });

  const deleteDeckMutation = useMutation({
    mutationFn: () => deleteDeck(deckId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_DECKS_KEY });
      notifications.show({ message: 'Колода удалена', color: 'teal' });
      void navigate('/admin/decks');
    },
    onError: fail('Не удалось удалить колоду'),
  });

  if (query.isLoading) return <LoadingState label="Открываем колоду..." />;
  if (query.isError || !deck) return <ErrorState onRetry={() => void query.refetch()} />;

  const published = deck.status === 'published';

  return (
    <>
      <PageHeader
        eyebrow="Администрирование · колоды"
        title={deck.title}
        description={`${deck.cardCount} карт · ${deck.kind === 'game' ? 'игровая колода' : 'обычная колода'}`}
        actions={
          <Group gap="sm">
            <Badge size="lg" variant={published ? 'light' : 'default'} color={published ? 'teal' : 'gray'}>
              {DECK_STATUS_LABELS[deck.status]}
            </Badge>
            <Button
              component={Link}
              to="/admin/decks"
              variant="default"
              leftSection={<IconArrowLeft size={15} />}
            >
              К списку
            </Button>
          </Group>
        }
      />

      <Tabs defaultValue={initialTab}>
        <Tabs.List>
          <Tabs.Tab value="cards" leftSection={<IconCards size={15} />}>
            Карты
          </Tabs.Tab>
          <Tabs.Tab value="access">Доступ</Tabs.Tab>
          <Tabs.Tab value="settings">Настройки</Tabs.Tab>
        </Tabs.List>

        {/* ------------------------------- cards ------------------------------ */}
        <Tabs.Panel value="cards" pt="lg">
          <Stack gap="lg">
            <Dropzone
              onDrop={(files) => importMutation.mutate(files)}
              accept={[...UPLOAD_IMAGE_MIME_TYPES]}
              loading={importMutation.isPending}
              maxSize={8 * 1024 * 1024}
              radius="lg"
              style={{ borderColor: 'var(--border-subtle)' }}
            >
              <Group justify="center" gap="md" mih={110} style={{ pointerEvents: 'none' }}>
                <Dropzone.Accept>
                  <IconUpload size={34} stroke={1.4} color="var(--accent)" />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={34} stroke={1.4} color="var(--danger)" />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconUpload size={34} stroke={1.4} color="var(--text-subtle)" />
                </Dropzone.Idle>
                <Stack gap={2}>
                  <Text fw={600}>Перетащите изображения карт или нажмите</Text>
                  <Text size="sm" c="var(--text-muted)">
                    Можно выбрать сразу несколько. WebP, JPEG, PNG, AVIF или GIF, до 8 МБ каждая.
                    Порядок загрузки станет порядком карт.
                  </Text>
                </Stack>
              </Group>
            </Dropzone>

            {deck.cards.length === 0 ? (
              <EmptyState
                icon={<IconCards size={28} stroke={1.5} />}
                title="В колоде пока нет карт"
                description="Загрузите изображения — колоду нельзя опубликовать, пока она пустая."
              />
            ) : (
              <Stack gap="sm">
                <Group justify="space-between">
                  <Title order={4}>Порядок карт</Title>
                  <Text size="sm" c="var(--text-muted)">
                    Перетащите карту за ручку, чтобы изменить порядок
                  </Text>
                </Group>
                <CardSorter
                  cards={deck.cards}
                  onReorder={(cardIds) => reorderMutation.mutate(cardIds)}
                  onDelete={(cardId) =>
                    modals.openConfirmModal({
                      title: 'Удалить карту?',
                      children: <Text size="sm">Изображение останется на диске, карта исчезнет из колоды.</Text>,
                      labels: { confirm: 'Удалить', cancel: 'Отмена' },
                      confirmProps: { color: 'red' },
                      onConfirm: () => deleteCardMutation.mutate(cardId),
                    })
                  }
                />
              </Stack>
            )}
          </Stack>
        </Tabs.Panel>

        {/* ------------------------------ access ------------------------------ */}
        <Tabs.Panel value="access" pt="lg">
          <Paper withBorder p="lg" radius="lg" maw={640} style={{ borderColor: 'var(--border-subtle)' }}>
            <Stack gap="md">
              <Stack gap={2}>
                <Title order={4}>Кому доступна колода</Title>
                <Text size="sm" c="var(--text-muted)">
                  Достаточно выполнить любое одно правило.
                </Text>
              </Stack>

              <AccessRulesEditor
                value={deck.accessRules}
                requiredGameId={deck.accessRequiredGameId}
                games={games}
                saving={accessMutation.isPending}
                onSave={(rules, gameId) => accessMutation.mutate({ rules, gameId })}
              />
            </Stack>
          </Paper>
        </Tabs.Panel>

        {/* ----------------------------- settings ----------------------------- */}
        <Tabs.Panel value="settings" pt="lg">
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
            <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
              <Stack gap="md">
                <Title order={4}>Основное</Title>

                <TextInput
                  label="Название"
                  value={title ?? deck.title}
                  onChange={(event) => setTitle(event.currentTarget.value)}
                />
                <Textarea
                  label="Описание"
                  minRows={3}
                  value={description ?? deck.description ?? ''}
                  onChange={(event) => setDescription(event.currentTarget.value)}
                />

                <Group>
                  <Button
                    size="sm"
                    loading={updateMutation.isPending}
                    disabled={title === null && description === null}
                    onClick={() =>
                      updateMutation.mutate({
                        ...(title !== null ? { title } : {}),
                        ...(description !== null ? { description } : {}),
                      })
                    }
                  >
                    Сохранить
                  </Button>
                </Group>

                <Switch
                  label="Доступна на свободном столе"
                  description="Обычные колоды появляются в «Авторской сессии» без правок кода"
                  checked={deck.availableInFreeform}
                  onChange={(event) =>
                    updateMutation.mutate({ availableInFreeform: event.currentTarget.checked })
                  }
                />

                <Switch
                  label="Карта не выпадает дважды в одной сессии"
                  description="Иначе карта возвращается в колоду после показа"
                  checked={deck.drawMode === 'unique_per_session'}
                  onChange={(event) =>
                    updateMutation.mutate({
                      drawMode: event.currentTarget.checked
                        ? 'unique_per_session'
                        : 'returns_to_deck',
                    })
                  }
                />
              </Stack>
            </Paper>

            <Stack gap="lg">
              <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
                <Stack gap="md">
                  <Title order={4}>Изображения</Title>
                  <SimpleGrid cols={2} spacing="md">
                    <CoverPicker
                      label="Обложка"
                      src={deck.coverImage}
                      loading={coverMutation.isPending}
                      onPick={(file) => coverMutation.mutate({ file, field: 'coverImage' })}
                    />
                    <CoverPicker
                      label="Рубашка карт"
                      src={deck.cardBackImage}
                      loading={coverMutation.isPending}
                      onPick={(file) => coverMutation.mutate({ file, field: 'cardBackImage' })}
                    />
                  </SimpleGrid>
                </Stack>
              </Paper>

              <Paper withBorder p="lg" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
                <Stack gap="md">
                  <Title order={4}>Публикация</Title>

                  {deck.cards.length === 0 ? (
                    <Alert variant="light" color="gold" icon={<IconAlertTriangle size={16} />}>
                      Нельзя опубликовать колоду без карт.
                    </Alert>
                  ) : deck.accessRules.length === 0 && !published ? (
                    <Alert variant="light" color="gold" icon={<IconAlertTriangle size={16} />}>
                      Правила доступа не заданы — после публикации колоду увидит только
                      администратор.
                    </Alert>
                  ) : null}

                  <Group>
                    <Button
                      variant={published ? 'default' : 'filled'}
                      loading={updateMutation.isPending}
                      disabled={!published && deck.cards.length === 0}
                      onClick={() =>
                        updateMutation.mutate({ status: published ? 'draft' : 'published' })
                      }
                    >
                      {published ? 'Снять с публикации' : 'Опубликовать'}
                    </Button>

                    <Button
                      variant="light"
                      color="red"
                      leftSection={<IconTrash size={15} />}
                      onClick={() =>
                        modals.openConfirmModal({
                          title: 'Удалить колоду?',
                          children: (
                            <Text size="sm">
                              Колода «{deck.title}» и её {deck.cardCount} карт будут удалены. Это
                              действие необратимо.
                            </Text>
                          ),
                          labels: { confirm: 'Удалить', cancel: 'Отмена' },
                          confirmProps: { color: 'red' },
                          onConfirm: () => deleteDeckMutation.mutate(),
                        })
                      }
                    >
                      Удалить колоду
                    </Button>
                  </Group>
                </Stack>
              </Paper>
            </Stack>
          </SimpleGrid>
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

function CoverPicker({
  label,
  src,
  loading,
  onPick,
}: {
  label: string;
  src: string | null;
  loading: boolean;
  onPick: (file: File) => void;
}) {
  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>
        {label}
      </Text>
      {/* Обложку и рубашку показываем целиком — форма у них бывает любая. */}
      <AssetImage src={src} alt={label} ratio="7 / 12" fit="contain" fallbackLabel="Не задано" />
      <Dropzone
        onDrop={(files) => files[0] && onPick(files[0])}
        accept={[...UPLOAD_IMAGE_MIME_TYPES]}
        loading={loading}
        maxFiles={1}
        p="xs"
        radius="md"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <Text size="xs" ta="center" c="var(--text-muted)" style={{ pointerEvents: 'none' }}>
          Заменить
        </Text>
      </Dropzone>
    </Stack>
  );
}
